/**
 * Post-Checkout return — server activates subscription, then redirect to dashboard.
 * When the session cookie is missing (cross-host redirect), show paid sign-in prompt.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { completeBillingReturn, fetchBillingReturnContext } from '../../api/billing'
import { useAuth } from '../../context/AuthContext'
import '../../components/billing/subscriptionBilling.css'

export default function BillingReturnScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, loading } = useAuth()
  const sessionId = searchParams.get('session_id')
  const [error, setError] = useState<string | null>(null)
  const [paidEmail, setPaidEmail] = useState<string | null>(null)
  const [needsSignIn, setNeedsSignIn] = useState(false)

  useEffect(() => {
    if (!sessionId) {
      setError('Missing checkout session')
      return
    }
    if (loading) return

    let cancelled = false
    void (async () => {
      try {
        if (!user) {
          const ctx = await fetchBillingReturnContext(sessionId)
          if (cancelled) return
          if (!ctx.paid) {
            setError('Payment was not completed. Return to pricing to try again.')
            return
          }
          setPaidEmail(ctx.email || null)
          setNeedsSignIn(true)
          return
        }
        await completeBillingReturn(sessionId)
        await queryClient.invalidateQueries({ queryKey: ['billing-status'] })
        if (!cancelled) navigate('/dashboard', { replace: true })
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : 'Could not complete checkout'
          if (!user && /auth|session|401|403/i.test(msg)) {
            try {
              const ctx = await fetchBillingReturnContext(sessionId)
              if (!cancelled && ctx.paid) {
                setPaidEmail(ctx.email || null)
                setNeedsSignIn(true)
                return
              }
            } catch {
              // fall through
            }
          }
          setError(msg)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [sessionId, navigate, queryClient, user, loading])

  if (needsSignIn) {
    const loginHref = paidEmail
      ? `/login?email=${encodeURIComponent(paidEmail)}&next=${encodeURIComponent('/dashboard')}`
      : `/login?next=${encodeURIComponent('/dashboard')}`
    return (
      <div className="subscription-pricing-page" data-testid="billing-return-page">
        <div className="subscription-pricing-card">
          <h1>You&apos;re paid — sign in to continue</h1>
          <p className="subscription-pricing-lead">
            Your subscription is active. Sign in with the same email you used at checkout to open your dashboard.
          </p>
          <Link className="btn b-pri" to={loginHref} data-testid="billing-return-sign-in">
            Sign in to continue
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="subscription-pricing-page" data-testid="billing-return-page">
      <div className="subscription-pricing-card">
        <h1>Completing your subscription…</h1>
        {error ? <p className="subscription-pricing-error">{error}</p> : <p className="subscription-pricing-lead">One moment — redirecting to your dashboard.</p>}
      </div>
    </div>
  )
}
